import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { Button } from 'components'
import { useAnalytics } from 'hooks'
import { openPaywall } from 'modules/billing'
import { useAppDispatch } from 'modules/core'
import { useWeeklyQueueStore } from '../store/weekly-queue-store'

/**
 * Slim attention strip above the creation hero. Renders ONLY when something
 * actually needs the user (posts awaiting approval, or the whole week held on
 * billing) — ambient status never jumps the queue, urgent things do. The
 * approvals CTA expands the week floor where the approve affordances live;
 * the billing-hold CTA opens the paywall (the highest-intent conversion
 * moment: posts are ready and waiting). Design:
 * playbook/product/home-creation-moment/DESIGN.md.
 */
export function AttentionStrip({ onReview }: { onReview: () => void }) {
  const { track } = useAnalytics()
  const dispatch = useAppDispatch()
  const pendingApprovalCount = useWeeklyQueueStore(s => s.pendingApprovalCount)
  const billingHold = useWeeklyQueueStore(s => s.billingHold)

  // Billing hold outranks approvals: approving changes nothing while the
  // sweep's billing gate holds every publish.
  if (billingHold) {
    return (
      <div
        data-testid="billing-hold-strip"
        className="flex items-center justify-between gap-3 rounded-[10px] border border-amber-400/40 bg-amber-400/10 px-4 py-2.5"
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <span
            className="h-1.5 w-1.5 flex-shrink-0 animate-pulse rounded-full bg-amber-400"
            aria-hidden="true"
          />
          <span className="truncate text-[13.5px] text-paper">
            Your scheduled posts are on hold. An active plan is needed to publish.
          </span>
        </div>
        <Button
          variant="t3-tertiary"
          size="t3-sm"
          onClick={() => {
            track(AnalyticsEvents.Home.HOME_ATTENTION_STRIP_CTA_TAPPED, {
              kind: 'billing_hold',
              count: pendingApprovalCount,
            })
            dispatch(openPaywall({ source: 'subscription_gate' }))
          }}
        >
          Reactivate
        </Button>
      </div>
    )
  }

  if (pendingApprovalCount <= 0) return null

  return (
    <div className="flex items-center justify-between gap-3 rounded-[10px] border border-line-bright bg-bg-2 px-4 py-2.5">
      <div className="flex min-w-0 items-center gap-2.5">
        <span
          className="h-1.5 w-1.5 flex-shrink-0 animate-pulse rounded-full bg-acid"
          aria-hidden="true"
        />
        <span className="truncate text-[13.5px] text-paper">
          {pendingApprovalCount === 1
            ? '1 post needs your approval'
            : `${pendingApprovalCount} posts need your approval`}
        </span>
      </div>
      <Button
        variant="t3-tertiary"
        size="t3-sm"
        onClick={() => {
          track(AnalyticsEvents.Home.HOME_ATTENTION_STRIP_CTA_TAPPED, {
            kind: 'approvals',
            count: pendingApprovalCount,
          })
          onReview()
        }}
      >
        Review
      </Button>
    </div>
  )
}
